/**
 * Table and dining-session lifecycles, plus the floor-map projection.
 *
 * WHY a table has both a machine and a derive function:
 * `tables.status` is a *cached projection*, not the source of truth. The truth
 * is spread across the open dining session, the orders on it, the unserved
 * items and the open service requests — all of which change concurrently from
 * the guest's phone, the POS and the KDS. If the POS tried to maintain the
 * status by applying edits in order, two simultaneous events would leave a
 * permanently wrong colour on the floor map.
 *
 * So:
 * - {@link tableMachine} covers the moves a human genuinely *decides*
 *   (reserve, seat, lock, take out of service, finish cleaning). Those are real
 *   stored transitions and are permission-guarded.
 * - {@link deriveTableStatus} recomputes everything else from facts after any
 *   change. It is pure, so the Durable Object, the Worker and an offline POS
 *   all reach the same colour for the same facts.
 *
 * Statuses mirror the CHECK constraints in `0002_organization.sql` (tables) and
 * `0006_orders.sql` (dining_sessions).
 */

import { StateMachine } from './state-machine.js';
import type { Transition } from './state-machine.js';

/** Table statuses exactly as constrained in `tables.status`. */
export const TABLE_STATUSES = [
  'EMPTY',
  'RESERVED',
  'OCCUPIED',
  'ORDERING',
  'WAITING_CONFIRMATION',
  'WAITING_FOOD',
  'PARTIALLY_SERVED',
  'FULLY_SERVED',
  'HELP_REQUESTED',
  'PAYMENT_REQUESTED',
  'PAYMENT_PROCESSING',
  'NEEDS_CLEANING',
  'SETTING_UP',
  'LOCKED',
  'OUT_OF_SERVICE',
] as const;

/** Union of table statuses. */
export type TableStatus = (typeof TABLE_STATUSES)[number];

/** Table-level events. */
export const TABLE_EVENTS = [
  'RESERVE',
  'CANCEL_RESERVATION',
  'SEAT',
  'START_ORDERING',
  'SUBMIT_ORDER',
  'CONFIRM_ORDER',
  'SERVE_PARTIAL',
  'SERVE_ALL',
  'REQUEST_HELP',
  'RESOLVE_HELP',
  'REQUEST_PAYMENT',
  'START_PAYMENT',
  'PAYMENT_FAILED',
  'COMPLETE_PAYMENT',
  'CLOSE',
  'FINISH_CLEANING',
  'START_SETUP',
  'FINISH_SETUP',
  'LOCK',
  'UNLOCK',
  'TAKE_OUT_OF_SERVICE',
  'RETURN_TO_SERVICE',
] as const;

/** Union of table events. */
export type TableEvent = (typeof TABLE_EVENTS)[number];

/**
 * Statuses that mean "guests are sitting here right now".
 *
 * Exported because several rules key off occupancy (QR ordering allowed,
 * reservation blocked, cleaning task not yet dispatchable) and each of them
 * re-listing the six statuses is how they drift apart.
 */
export const OCCUPIED_TABLE_STATUSES: readonly TableStatus[] = [
  'OCCUPIED',
  'ORDERING',
  'WAITING_CONFIRMATION',
  'WAITING_FOOD',
  'PARTIALLY_SERVED',
  'FULLY_SERVED',
];

const OCCUPIED_PLUS_HELP: readonly TableStatus[] = [...OCCUPIED_TABLE_STATUSES, 'HELP_REQUESTED'];

/** Small helper so fan-out edges (LOCK from anywhere) stay declarative. */
function fanIn(
  froms: readonly TableStatus[],
  event: TableEvent,
  to: TableStatus,
  extra: { permission?: string; guardName?: string } = {},
): Transition<TableStatus, TableEvent>[] {
  return froms.map((from) => ({ from, event, to, ...extra }));
}

/**
 * The table lifecycle graph.
 *
 * WHY UNLOCK and RESOLVE_HELP land on a "neutral" state (EMPTY / OCCUPIED)
 * rather than restoring the previous one: a state machine has no memory, and
 * inventing one here would duplicate the projection. The next
 * {@link deriveTableStatus} pass — which runs on every mutation — immediately
 * corrects the display, so the neutral landing is never visible to staff.
 */
export const TABLE_TRANSITIONS: readonly Transition<TableStatus, TableEvent>[] = [
  { from: 'EMPTY', event: 'RESERVE', to: 'RESERVED', permission: 'reservation.manage' },
  { from: 'RESERVED', event: 'CANCEL_RESERVATION', to: 'EMPTY', permission: 'reservation.manage' },

  ...fanIn(['EMPTY', 'RESERVED'], 'SEAT', 'OCCUPIED', { permission: 'table.open' }),

  ...fanIn(['OCCUPIED', 'WAITING_FOOD', 'PARTIALLY_SERVED', 'FULLY_SERVED'], 'START_ORDERING', 'ORDERING'),
  ...fanIn(OCCUPIED_TABLE_STATUSES, 'SUBMIT_ORDER', 'WAITING_CONFIRMATION'),
  { from: 'WAITING_CONFIRMATION', event: 'CONFIRM_ORDER', to: 'WAITING_FOOD', permission: 'order.confirm' },

  ...fanIn(['WAITING_FOOD', 'PARTIALLY_SERVED'], 'SERVE_PARTIAL', 'PARTIALLY_SERVED'),
  ...fanIn(['WAITING_FOOD', 'PARTIALLY_SERVED'], 'SERVE_ALL', 'FULLY_SERVED'),

  ...fanIn(OCCUPIED_TABLE_STATUSES, 'REQUEST_HELP', 'HELP_REQUESTED'),
  { from: 'HELP_REQUESTED', event: 'RESOLVE_HELP', to: 'OCCUPIED' },

  ...fanIn(OCCUPIED_PLUS_HELP, 'REQUEST_PAYMENT', 'PAYMENT_REQUESTED'),
  ...fanIn([...OCCUPIED_PLUS_HELP, 'PAYMENT_REQUESTED'], 'START_PAYMENT', 'PAYMENT_PROCESSING', {
    permission: 'payment.receive',
  }),
  { from: 'PAYMENT_PROCESSING', event: 'PAYMENT_FAILED', to: 'PAYMENT_REQUESTED' },
  { from: 'PAYMENT_PROCESSING', event: 'COMPLETE_PAYMENT', to: 'NEEDS_CLEANING', permission: 'payment.receive' },

  // Closing without payment (comped, staff meal, guest walked) is deliberately
  // possible but sits behind table.close and always leaves the table dirty —
  // a table is never released straight to EMPTY without someone confirming it.
  ...fanIn([...OCCUPIED_PLUS_HELP, 'PAYMENT_REQUESTED'], 'CLOSE', 'NEEDS_CLEANING', { permission: 'table.close' }),

  { from: 'NEEDS_CLEANING', event: 'FINISH_CLEANING', to: 'SETTING_UP', permission: 'table.clean' },
  { from: 'EMPTY', event: 'START_SETUP', to: 'SETTING_UP', permission: 'table.setup' },
  { from: 'SETTING_UP', event: 'FINISH_SETUP', to: 'EMPTY', permission: 'table.setup' },

  ...fanIn(
    TABLE_STATUSES.filter((s): s is TableStatus => s !== 'LOCKED' && s !== 'OUT_OF_SERVICE'),
    'LOCK',
    'LOCKED',
    { permission: 'table.manage' },
  ),
  { from: 'LOCKED', event: 'UNLOCK', to: 'EMPTY', permission: 'table.manage' },

  ...fanIn(
    TABLE_STATUSES.filter((s): s is TableStatus => s !== 'OUT_OF_SERVICE'),
    'TAKE_OUT_OF_SERVICE',
    'OUT_OF_SERVICE',
    { permission: 'table.manage', guardName: 'requiresReason' },
  ),
  { from: 'OUT_OF_SERVICE', event: 'RETURN_TO_SERVICE', to: 'NEEDS_CLEANING', permission: 'table.manage' },
];

/**
 * The shared table lifecycle.
 * No terminal states: a table is a physical asset that always comes back.
 */
export const tableMachine = new StateMachine<TableStatus, TableEvent>(TABLE_TRANSITIONS, { name: 'bàn' });

/** Dining-session statuses exactly as constrained in `dining_sessions.status`. */
export const DINING_SESSION_STATUSES = [
  'OPEN',
  'ORDERING',
  'WAITING_CONFIRMATION',
  'SERVING',
  'PAYMENT_REQUESTED',
  'PAYMENT_PROCESSING',
  'CLOSED',
  'CANCELLED',
  'LOCKED',
] as const;

/** Union of dining-session statuses. */
export type DiningSessionStatus = (typeof DINING_SESSION_STATUSES)[number];

/** Dining-session events. Deliberately a subset of the table vocabulary. */
export const DINING_SESSION_EVENTS = [
  'START_ORDERING',
  'SUBMIT_ORDER',
  'CONFIRM_ORDER',
  'REQUEST_PAYMENT',
  'START_PAYMENT',
  'PAYMENT_FAILED',
  'COMPLETE_PAYMENT',
  'CLOSE',
  'CANCEL',
  'LOCK',
  'UNLOCK',
] as const;

/** Union of dining-session events. */
export type DiningSessionEvent = (typeof DINING_SESSION_EVENTS)[number];

const ACTIVE_SESSION_STATUSES: readonly DiningSessionStatus[] = [
  'OPEN',
  'ORDERING',
  'WAITING_CONFIRMATION',
  'SERVING',
];

function sessionFanIn(
  froms: readonly DiningSessionStatus[],
  event: DiningSessionEvent,
  to: DiningSessionStatus,
  extra: { permission?: string; guardName?: string } = {},
): Transition<DiningSessionStatus, DiningSessionEvent>[] {
  return froms.map((from) => ({ from, event, to, ...extra }));
}

/**
 * The dining-session lifecycle graph.
 *
 * WHY the session is modelled at all when the table already has a status: the
 * session is the *billing* unit. It survives a table transfer or a merge, it
 * owns the guest's chosen locale for the QR menu, and it is what a receipt is
 * attached to. The table is furniture; the session is the visit.
 */
export const DINING_SESSION_TRANSITIONS: readonly Transition<DiningSessionStatus, DiningSessionEvent>[] = [
  ...sessionFanIn(['OPEN', 'SERVING'], 'START_ORDERING', 'ORDERING'),
  ...sessionFanIn(['OPEN', 'ORDERING', 'SERVING'], 'SUBMIT_ORDER', 'WAITING_CONFIRMATION'),
  { from: 'WAITING_CONFIRMATION', event: 'CONFIRM_ORDER', to: 'SERVING', permission: 'order.confirm' },

  ...sessionFanIn(ACTIVE_SESSION_STATUSES, 'REQUEST_PAYMENT', 'PAYMENT_REQUESTED'),
  ...sessionFanIn([...ACTIVE_SESSION_STATUSES, 'PAYMENT_REQUESTED'], 'START_PAYMENT', 'PAYMENT_PROCESSING', {
    permission: 'payment.receive',
  }),
  { from: 'PAYMENT_PROCESSING', event: 'PAYMENT_FAILED', to: 'PAYMENT_REQUESTED' },
  { from: 'PAYMENT_PROCESSING', event: 'COMPLETE_PAYMENT', to: 'CLOSED', permission: 'payment.receive' },

  ...sessionFanIn([...ACTIVE_SESSION_STATUSES, 'PAYMENT_REQUESTED'], 'CLOSE', 'CLOSED', {
    permission: 'table.close',
  }),
  // CANCEL is for a session opened by mistake; anything already billed must be
  // closed (and refunded) instead, hence the guard.
  ...sessionFanIn(ACTIVE_SESSION_STATUSES, 'CANCEL', 'CANCELLED', {
    permission: 'table.close',
    guardName: 'noBilledItems',
  }),

  ...sessionFanIn([...ACTIVE_SESSION_STATUSES, 'PAYMENT_REQUESTED', 'PAYMENT_PROCESSING'], 'LOCK', 'LOCKED', {
    permission: 'table.manage',
  }),
  { from: 'LOCKED', event: 'UNLOCK', to: 'OPEN', permission: 'table.manage' },
];

/** The shared dining-session lifecycle. CLOSED/CANCELLED end the visit for good. */
export const diningSessionMachine = new StateMachine<DiningSessionStatus, DiningSessionEvent>(
  DINING_SESSION_TRANSITIONS,
  { name: 'phiên bàn', terminalStates: ['CLOSED', 'CANCELLED'] },
);

/**
 * The observable facts a table's display status is computed from.
 *
 * Every field is something the caller can read with a single indexed query —
 * that is the point. If a field required a judgement call, staff would have to
 * make it, and the floor map would stop being trustworthy.
 */
export interface TableStatusFacts {
  /** `tables.is_active = 0` — decommissioned furniture. */
  readonly isActive: boolean;
  /** Manually taken out of service (broken leg, renovation). */
  readonly outOfService: boolean;
  /** Administratively frozen (stock-take, dispute, shift handover). */
  readonly locked: boolean;
  /** A staff member is currently laying the table. */
  readonly isSettingUp: boolean;
  /** Guests left, nobody has confirmed the table is clean yet. */
  readonly needsCleaning: boolean;
  /** A future reservation currently holds this table. */
  readonly hasReservation: boolean;
  /** An open (non-CLOSED, non-CANCELLED) dining session exists. */
  readonly hasOpenSession: boolean;
  /** A guest device is on the ordering page right now (presence, not persistence). */
  readonly isGuestOrdering: boolean;
  /** Orders sitting in PENDING_CONFIRMATION — staff must approve them. */
  readonly pendingConfirmationOrderCount: number;
  /** Items the kitchen still owes (see `isItemOutstanding`). */
  readonly unservedItemCount: number;
  /** Items already delivered to the table. */
  readonly servedItemCount: number;
  /** An unresolved service request with code CALL_STAFF (or any help code). */
  readonly hasOpenCallStaffRequest: boolean;
  /** An unresolved service request with code REQUEST_PAYMENT. */
  readonly hasOpenPaymentRequest: boolean;
  /** A payment attempt is in flight (cash drawer open, QR/card pending). */
  readonly paymentInProgress: boolean;
}

/**
 * Compute the status the POS floor map should paint for a table.
 *
 * PRECEDENCE (first match wins) — read top to bottom:
 *
 *  1. OUT_OF_SERVICE   — furniture is unusable; nothing else is relevant.
 *  2. LOCKED           — an admin froze the table; staff must not act on it.
 *  3. SETTING_UP       — someone is physically at the table doing the setup.
 *  4. NEEDS_CLEANING   — only meaningful with no open session; a dirty table
 *                        with new guests seated is an occupied table.
 *  5. PAYMENT_PROCESSING — money is moving. Highest live-service priority
 *                        because interrupting a payment loses cash.
 *  6. PAYMENT_REQUESTED  — the guest asked for the bill; this blocks table
 *                        turnover, which is the scarcest resource at peak.
 *  7. HELP_REQUESTED   — the guest called staff.
 *                        (5–7 outrank every food state below: an unattended
 *                        guest is always more urgent than a pending dish,
 *                        because the dish already has an owner in the kitchen
 *                        while the guest has nobody.)
 *  8. WAITING_CONFIRMATION — a QR order is queued for staff approval; nothing
 *                        is cooking yet, so this is time-critical.
 *  9. ORDERING         — a guest device is actively browsing the menu.
 * 10. WAITING_FOOD / PARTIALLY_SERVED / FULLY_SERVED — food progress.
 * 11. OCCUPIED         — seated, nothing ordered yet.
 * 12. RESERVED / EMPTY.
 *
 * WHY pure: this runs in the Durable Object that fans out realtime table
 * updates, in the Worker that answers the floor-map query, and in the POS while
 * offline. Same facts must give the same colour in all three.
 */
export function deriveTableStatus(facts: TableStatusFacts): TableStatus {
  if (!facts.isActive || facts.outOfService) return 'OUT_OF_SERVICE';
  if (facts.locked) return 'LOCKED';
  if (facts.isSettingUp) return 'SETTING_UP';
  if (facts.needsCleaning && !facts.hasOpenSession) return 'NEEDS_CLEANING';

  if (facts.paymentInProgress) return 'PAYMENT_PROCESSING';
  if (facts.hasOpenPaymentRequest) return 'PAYMENT_REQUESTED';
  if (facts.hasOpenCallStaffRequest) return 'HELP_REQUESTED';

  if (!facts.hasOpenSession) return facts.hasReservation ? 'RESERVED' : 'EMPTY';

  if (facts.pendingConfirmationOrderCount > 0) return 'WAITING_CONFIRMATION';
  if (facts.isGuestOrdering) return 'ORDERING';

  const unserved = Math.max(0, facts.unservedItemCount);
  const served = Math.max(0, facts.servedItemCount);
  if (unserved > 0) return served > 0 ? 'PARTIALLY_SERVED' : 'WAITING_FOOD';
  if (served > 0) return 'FULLY_SERVED';

  return 'OCCUPIED';
}

/** Are guests seated here? Used to block reservations and QR re-binding. */
export function isTableOccupied(status: TableStatus): boolean {
  return (
    OCCUPIED_TABLE_STATUSES.includes(status) ||
    status === 'HELP_REQUESTED' ||
    status === 'PAYMENT_REQUESTED' ||
    status === 'PAYMENT_PROCESSING'
  );
}

/** Can a new dining session be opened on this table right now? */
export function isTableSeatable(status: TableStatus): boolean {
  return status === 'EMPTY' || status === 'RESERVED';
}
